import type { LogLine } from '@pm2-monitor/shared';
import { stripAnsi } from '../../lib/ansi';
import { formatClock } from '../../lib/format';
import { levelPill, tabularNums } from '../ui';

export type LogRowVariant = 'compact' | 'full' | 'drawer';

/**
 * One line of merged pm2 output. `compact` truncates to a single line for the
 * Overview digest; `full` lets long messages wrap, because the Logs view is
 * where output is actually read.
 */
export function LogRow({
  line,
  showProcess = true,
  variant = 'full',
}: {
  line: LogLine;
  showProcess?: boolean;
  variant?: LogRowVariant;
}) {
  const pill = levelPill(line.level);
  const compact = variant === 'compact';
  const drawer = variant === 'drawer';

  return (
    <div
      style={{
        display: 'flex',
        gap: drawer ? 12 : 16,
        padding: '8px 0',
        fontSize: 13,
        alignItems: 'baseline',
        ...(compact
          ? { borderTop: '1px solid var(--line-soft)' }
          : { borderBottom: '1px solid var(--line-soft)' }),
      }}
    >
      <div
        style={{
          width: drawer ? 58 : 62,
          flex: 'none',
          color: 'var(--faint)',
          ...tabularNums,
        }}
      >
        {formatClock(line.timestamp)}
      </div>

      {showProcess ? (
        <div
          style={{
            width: 104,
            flex: 'none',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={line.processName}
        >
          {line.processName}
        </div>
      ) : null}

      <div style={compact || drawer ? { flex: 'none' } : { width: 64, flex: 'none' }}>
        <span
          style={{
            padding: '2px 9px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            background: pill.bg,
            color: pill.fg,
          }}
        >
          {line.level}
        </span>
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          color: 'var(--muted)',
          ...(compact
            ? {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }
            : { overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }),
        }}
      >
        {stripAnsi(line.message)}
      </div>
    </div>
  );
}
